// THE picker control. One search box, one list, one popover — for every place the
// customer chooses from a list long enough that scanning it is the work.
//
// One control, not two. A separate "Search your items" textbox sitting ABOVE a
// native <select> reads as two questions and behaves like neither: the box
// narrows a list the customer cannot see, and the list they open has no search in
// it. The search belongs INSIDE the thing it searches.
//
// Its sibling in nexus_erp is the same control on a different stack —
// `nexus_erp/packages/frontend/src/components/shared/SearchableSelect.tsx` with
// its MaterialPicker wrapper. Keep the two behaving alike; this one is
// hand-rolled because the portal carries no Radix or cmdk and is not taking them
// on for one control.
//
// Matching is the SHARED token rule (shared util/search-tokens.ts), the same rule
// Nexus applies server-side to the item master. Every token must hit the option's
// label, its hint, or one of its hidden keywords — so a row is findable by SKU,
// name, description or unit, and a picker can never narrow away a row the server
// just returned as a match.
//
// Accessibility, deliberately and not incidentally: a native <select> comes with
// keyboard navigation, type-ahead, screen-reader semantics and a correct touch
// picker, and all four have to be earned here rather than assumed. The trigger is
// a button that says what is chosen; opening it moves focus into a search input
// wired as the combobox (aria-expanded / aria-controls / aria-activedescendant)
// over a role="listbox". Arrow keys move the active option without moving focus,
// Enter takes it, Escape closes and returns focus to the trigger, and Tab away
// closes. The active option is scrolled into view, because keyboard movement that
// leaves the highlight off-screen is movement the customer cannot follow.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { matchesAllTokens, tokenizeSearch } from 'shared';

import { ChevronDownIcon } from './ControlIcons.js';

export type SearchableOption = {
  value: string;
  label: string;
  // A second, muted line under the label. For what the customer needs in order to
  // tell two similarly-named rows apart — an item's description, a kit's
  // component count. It is MATCHED as well as shown, which is what makes matching
  // on a description honest: the customer can see why the row came back.
  hint?: string;
  // Matched but never shown. For terms already on screen elsewhere in the row, or
  // too noisy to render, that the customer would still reasonably type.
  keywords?: readonly string[];
};

export function SearchableSelect(props: {
  id: string;
  value: string;
  options: readonly SearchableOption[];
  // The trigger's text when nothing is chosen, and the first row of the list —
  // choosing it clears the pick. A caller whose field is required passes copy
  // that does not offer an out ("Choose one…", not "None of these").
  emptyOptionLabel: string;
  // Placeholder and accessible name for the search box. Says what can be typed,
  // not "search".
  searchPlaceholder: string;
  noMatchLabel: string;
  // What the list says when it has NO options at all and nothing was typed — an
  // empty source, not a search that missed. Without it the open list is a lone
  // clear row, which reads as broken rather than as "there is nothing here yet".
  emptyListLabel?: string;
  // Shown under the list when the caller holds only the first page of a longer
  // source — the list is cut off, and saying so is what tells the customer the
  // row they cannot find is reached by typing, not missing. Undefined when the
  // caller holds the whole source.
  moreLabel?: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  // Told what the customer typed, for a list whose full set does not fit in one
  // response. The client-side narrowing still runs over the page in hand — it
  // keeps the list responsive between fetches — so a caller that wires this gets
  // both halves and a caller that does not is unchanged.
  onSearchChange?: (query: string) => void;
  // Shown in place of the no-match line while a server-backed list is in flight.
  // "Nothing matches" is a claim this control cannot make about a request that has
  // not come back yet.
  loading?: boolean;
  // An action the customer can take when the row they want is not in the list —
  // ask the source to look again. Rendered as the panel's last line whatever the
  // list holds (results, no match, or nothing), because "it isn't here" is a
  // judgement the customer makes, not one this control can. It fires only on a
  // click and the search text stays put, so the refreshed list comes back already
  // narrowed. While `busy` it reads as `busyLabel` and ignores further clicks.
  refresh?:
    | { label: string; busyLabel: string; busy: boolean; onRefresh: () => void }
    | undefined;
}): JSX.Element {
  const {
    id,
    value,
    options,
    emptyOptionLabel,
    searchPlaceholder,
    noMatchLabel,
    emptyListLabel,
    moreLabel,
    onChange,
    disabled,
    invalid,
    describedBy,
    onSearchChange,
    loading,
    refresh,
  } = props;
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const uid = useId();
  const listId = `${uid}-list`;
  const optionDomId = (index: number): string => `${uid}-opt-${index}`;

  // The clear row is offered as an ordinary option so one keyboard path covers
  // it, and it is never filtered out: it is not a row the customer is searching
  // for.
  const rows = useMemo(() => {
    const tokens = tokenizeSearch(query);
    const matched = options.filter((o) =>
      matchesAllTokens([o.label, o.hint, ...(o.keywords ?? [])], tokens),
    );
    return [{ value: '', label: emptyOptionLabel } as SearchableOption, ...matched];
  }, [options, query, emptyOptionLabel]);

  const selected = options.find((o) => o.value === value);
  // A stored pick whose row the caller has not supplied still has to read as
  // chosen. The trigger falls back to the raw key rather than to the placeholder,
  // because "nothing chosen" is a different and wronger thing to say.
  const triggerLabel = selected?.label ?? (value !== '' ? value : emptyOptionLabel);

  const close = useCallback(
    (restoreFocus: boolean): void => {
      setOpen(false);
      setQuery('');
      onSearchChange?.('');
      if (restoreFocus) triggerRef.current?.focus();
    },
    [onSearchChange],
  );

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Land on the current pick, not on the clear row: opening a picker to see what is
  // chosen should show it highlighted.
  //
  // Keyed on the pick becoming FINDABLE rather than on `open`, because a picker
  // opened while its first page is still in flight has no row to land on yet — and
  // an effect that only fired on open would leave the highlight on the clear row
  // once the page arrived. `seededFor` makes it fire exactly once per opening: not
  // again as the customer narrows the list, which would drag the highlight back to
  // the selection on every keystroke.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededFor.current = null;
      return;
    }
    if (seededFor.current === value) return;
    const at = rows.findIndex((r) => r.value === value);
    if (at < 0) return;
    seededFor.current = value;
    setActiveIndex(at);
  }, [open, rows, value]);

  // Clamp rather than reset when the list shrinks under the highlight: resetting
  // to the top on every keystroke fights a customer who is arrowing while typing.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(rows.length - 1, 0)));
  }, [rows.length]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, rows.length]);

  function commit(index: number): void {
    const row = rows[index];
    if (!row) return;
    onChange(row.value);
    close(true);
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => {
        const next = i + step;
        if (next < 0) return rows.length - 1;
        if (next >= rows.length) return 0;
        return next;
      });
      return;
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setActiveIndex(e.key === 'Home' ? 0 : rows.length - 1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(activeIndex);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    }
  }

  // Only the clear row left, and the customer has typed something: nothing they
  // asked for is here.
  const noMatch = loading !== true && query.trim() !== '' && rows.length === 1;
  const emptyList =
    emptyListLabel !== undefined && loading !== true && query.trim() === '' && options.length === 0;

  return (
    <div className="searchable-select" ref={rootRef}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`searchable-select__trigger${
          value === '' ? ' searchable-select__trigger--empty' : ''
        }${open ? ' searchable-select__trigger--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid === true ? true : undefined}
        aria-describedby={describedBy}
        disabled={disabled === true}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(e) => {
          if (open) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="searchable-select__value">{triggerLabel}</span>
        <ChevronDownIcon className="searchable-select__chevron" />
      </button>
      {open && (
        <div
          className="searchable-select__panel"
          // A click landing on the panel's own padding would otherwise blur the
          // search box and leave the list open with focus nowhere.
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) e.preventDefault();
          }}
        >
          <input
            ref={searchRef}
            className="searchable-select__search"
            type="text"
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-autocomplete="list"
            aria-label={searchPlaceholder}
            {...(rows[activeIndex] ? { 'aria-activedescendant': optionDomId(activeIndex) } : {})}
            placeholder={searchPlaceholder}
            value={query}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              onSearchChange?.(e.target.value);
            }}
            onKeyDown={onSearchKeyDown}
            onBlur={(e) => {
              // Tab away closes; a click INSIDE the panel does not.
              if (!rootRef.current?.contains(e.relatedTarget as Node)) close(false);
            }}
          />
          <ul className="searchable-select__list" id={listId} role="listbox" ref={listRef}>
            {rows.map((row, index) => (
              <li key={`${row.value}-${index}`}>
                <button
                  type="button"
                  id={optionDomId(index)}
                  role="option"
                  aria-selected={row.value === value}
                  data-active={index === activeIndex}
                  // Not a tab stop: the search box drives the options through
                  // aria-activedescendant, and a hundred tab stops would stand
                  // between it and the refresh action under the list.
                  tabIndex={-1}
                  className={`searchable-select__option${
                    row.value === value ? ' searchable-select__option--selected' : ''
                  }${index === activeIndex ? ' searchable-select__option--active' : ''}${
                    row.value === '' ? ' searchable-select__option--clear' : ''
                  }`}
                  // Pointer MOVE and not hover CSS: the highlight is the
                  // keyboard's active option, so the mouse has to move that same
                  // state or the two disagree about what Enter would take.
                  onMouseMove={() => setActiveIndex(index)}
                  // Keep focus in the search box so typing still works after a
                  // mouse-driven highlight.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(index)}
                >
                  <span className="searchable-select__option-label">{row.label}</span>
                  {row.hint !== undefined && row.hint !== '' && (
                    <span className="searchable-select__option-hint">{row.hint}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {/* Announced, not merely shown: both lines appear as a consequence of
              typing, so a customer who cannot see them appearing gets no signal
              about the list they are looking at. */}
          {loading === true && (
            <p className="searchable-select__status" role="status" aria-live="polite">
              {t('states.loading')}
            </p>
          )}
          {noMatch && (
            <p className="searchable-select__status" role="status" aria-live="polite">
              {noMatchLabel}
            </p>
          )}
          {emptyList && (
            <p className="searchable-select__status" role="status" aria-live="polite">
              {emptyListLabel}
            </p>
          )}
          {moreLabel !== undefined && loading !== true && !noMatch && (
            <p className="searchable-select__status" role="status" aria-live="polite">
              {moreLabel}
            </p>
          )}
          {refresh !== undefined && (
            <button
              type="button"
              className="link-button searchable-select__refresh"
              // Not `disabled`: a disabled button drops focus, and the blur would
              // close the panel under a keyboard user who just pressed it.
              aria-disabled={refresh.busy ? true : undefined}
              aria-busy={refresh.busy ? true : undefined}
              // Keep focus in the search box on a mouse click, as the options do.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!refresh.busy) refresh.onRefresh();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close(true);
                }
              }}
              onBlur={(e) => {
                if (!rootRef.current?.contains(e.relatedTarget as Node)) close(false);
              }}
            >
              {refresh.busy ? refresh.busyLabel : refresh.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
